export interface NamedBlueprintSuggestion {
  id?: string;
  name: string;
  confidence: number;
  reason: string;
}

export interface ResolvedBlueprintSuggestion<
  T extends { id: string; name: string },
> {
  blueprint: T;
  confidence: number;
  reason: string;
  best: boolean;
}

/** 0–1 confidence as a short percent label, e.g. 0.82 → "82%". */
export function blueprintSuggestionPercent(confidence: number): string {
  const ratio = confidence > 1 ? confidence / 100 : confidence;
  return `${Math.round(ratio * 100)}%`;
}

/**
 * Catalogue blueprints for suggestion ids, in suggestion order.
 * A suggestion with an id matches that row only. A name is used only when it
 * matches exactly one catalogue blueprint. The first match is the best match.
 */
export function resolveNamedBlueprintSuggestions<
  T extends { id: string; name: string },
>(
  blueprints: T[],
  suggestions: NamedBlueprintSuggestion[],
): ResolvedBlueprintSuggestion<T>[] {
  const resolved: ResolvedBlueprintSuggestion<T>[] = [];
  const usedIds = new Set<string>();

  for (const suggestion of suggestions) {
    const matches = catalogueMatches(blueprints, suggestion);
    if (matches.length !== 1) {
      continue;
    }
    const blueprint = matches[0];
    if (usedIds.has(blueprint.id)) {
      continue;
    }
    usedIds.add(blueprint.id);
    resolved.push({
      blueprint,
      confidence: suggestion.confidence,
      reason: suggestion.reason?.trim() ?? '',
      best: resolved.length === 0,
    });
  }

  return resolved;
}

function catalogueMatches<T extends { id: string; name: string }>(
  blueprints: T[],
  suggestion: NamedBlueprintSuggestion,
): T[] {
  const id = suggestion.id?.trim();
  if (id) {
    return blueprints.filter((blueprint) => String(blueprint.id) === id);
  }
  const name = suggestion.name.trim().toLowerCase();
  if (!name) {
    return [];
  }
  return blueprints.filter(
    (blueprint) => blueprint.name.trim().toLowerCase() === name,
  );
}

export function namedSuggestionsFromDrawingExtractions(
  drawings: {
    suggestions?: {
      blueprint?: {
        id?: string | number;
        name: string;
        confidence: number;
        reason?: string;
      } | null;
      alternate_blueprints?:
        | {
            id?: string | number;
            name: string;
            confidence: number;
            reason?: string;
          }[]
        | null;
    } | null;
  }[],
): NamedBlueprintSuggestion[] {
  const named: NamedBlueprintSuggestion[] = [];
  for (const drawing of drawings) {
    const suggestions = drawing.suggestions;
    if (!suggestions) {
      continue;
    }
    pushNamedSuggestion(named, suggestions.blueprint);
    for (const alternate of suggestions.alternate_blueprints ?? []) {
      pushNamedSuggestion(named, alternate);
    }
  }
  return named;
}

function pushNamedSuggestion(
  named: NamedBlueprintSuggestion[],
  suggestion:
    | {
        id?: string | number | null;
        name?: string | null;
        confidence: number;
        reason?: string;
      }
    | null
    | undefined,
): void {
  if (!suggestion) {
    return;
  }
  const id =
    suggestion.id == null || suggestion.id === ''
      ? undefined
      : String(suggestion.id);
  const name = suggestion.name?.trim() ?? '';
  if (!id && !name) {
    return;
  }
  named.push({
    id,
    name,
    confidence: suggestion.confidence,
    reason: suggestion.reason ?? '',
  });
}
